<template>
    <div>
        <PageTitle v-if="error" :title="$t('gracias.error_title')"/>
        <PageTitle v-else :title="$t('gracias.title')" sectionClass="buy_success" />
        <FirstBanner />

        <section class="padding-vertical-content pt-0">
            <b-container>
                <ContentOrLoading :class="loading ? 'p-5' : ''">
                    <b-row class="justify-content-center mx-0 mx-sm-2 mx-md-4" v-if="!loading">
                        <b-col cols="12" lg="10" v-if="error">
                            <div class="text-center">
                                <div class="h5">
                                    {{ $t("gracias.error") }}
                                </div>
                                <div>
                                    <p>{{ $t("gracias.mas_tarde") }}</p>
                                </div>
                                <router-link :to="{ name: 'front.home' }" class="button button effect-button mb-0 mt-3">
                                    {{ $t("gracias.volver") }}
                                </router-link>
                            </div>
                        </b-col>
                        <b-col cols="12" lg="10" v-else>
                            <div class="text-center">
                                <p class="h4 mt-2 mt-lg-0">
                                    {{ pageTitle }}
                                </p>
                                <div class="d-flex justify-content-center">
                                    <p class="mr-2 mt-1">
                                        {{ $t("gracias.n_pedido") }}:
                                    </p>
                                    <h4 id="n-pedido" data-cy="n-pedido">
                                        {{ n_pedido }}
                                    </h4>
                                </div>
                            </div>
                            <div class="text-center mb-4">
                                <router-link :to="ctaButtonRoute" class="button button effect-button mt-0">
                                    <span>{{ ctaButtonText }}</span>
                                </router-link>

                                <div class="h5" v-if="configuracion.aviso_gracias" v-html="configuracion.aviso_gracias"></div>
                            </div>

                            <div v-if="resumen.tipo === 'eventos'">
                                <DatosEntrada
                                    v-for="(entrada, index) in resumen.entradas"
                                    :key="index"
                                    :index="index"
                                    :entrada="entrada"
                                    :resumen="resumen"
                                    :sesion_fecha="resumen.sesion.fecha_inicio"
                                />
                            </div>
                            <div v-else-if="resumen.tipo === 'membresias' || resumen.tipo === 'renovacion'">
                                <DatosMembresia
                                    v-for="(membresia, index) in resumen.membresias"
                                    :key="membresia.id || index"
                                    :membresia="membresia"
                                    :index="index"
                                />
                            </div>
                            <div v-else>
                                <DatosAbono
                                    v-for="(abono, index) in resumen.abonados"
                                    :key="index"
                                    :abono="abono"
                                    :index="index"
                                />
                            </div>
                            <div v-if="resumen.tipo === 'eventos'" class="mt-2">
                                <p>
                                    <b>
                                        {{ $t('gracias.duda_incidencia', { email: manager.email_incidencias ? manager.email_incidencias : "info@compralaentrada.com" }) }}
                                    </b>
                                </p>
                                <p>
                                    <b>
                                        {{ $t('gracias.info_descarga_entradas') }}
                                    </b>
                                </p>
                            </div>
                        </b-col>
                    </b-row>
                </ContentOrLoading>
            </b-container>
        </section>
    </div>
</template>

<script>
    import PageTitle from "@/components/frontend/shared/PageTitle";
    import FirstBanner from "@/components/frontend/shared/FirstBanner";
    import DatosAbono from "@/components/frontend/sga/DatosAbono";
    import DatosEntrada from "@/components/frontend/sga/DatosEntrada";
    import DatosMembresia from "@/modules/front/sga/membresia/components/DatosMembresia.vue";
    import { useAbonoTracking } from '@/composables/front/analytics/useAbonoTracking';
    import { useEventoTracking } from '@/composables/front/analytics/useEventoTracking';

    export default {
        props: {
            session_id: {
                type: String,
                required: true
            },
            n_pedido: {
                type: [Number, String],
                required: true
            }
        },
        data() {
            return {
                resumen: null,
                loading: false,
                error: false,
                pollTimer: null
            }
        },
        methods: {
            getResumen() {
                if (this.pollTimer) {
                    clearTimeout(this.pollTimer);
                    this.pollTimer = null;
                }

                this.loading = true;

                const params = {
                    filters: {},
                    session_id: this.session_id,
                    tpv_order: this.n_pedido
                }

                this.$store.dispatch(this.config.getVuexAlias('sga.cart.resumen'), params).then(res => {
                    // El pedido aún se está procesando en SGA (p.ej. Bizum
                    // redirige antes de que el backend confirme): reintentar
                    // hasta que el resumen esté listo, como hace el flujo de
                    // eventos con response_code "wait".
                    if (res.data?.response_code === 'wait') {
                        this.pollTimer = setTimeout(() => {
                            this.getResumen();
                        }, 10000);
                        return;
                    }
                    this.resumen = res.data.data;
                    this.trackPurchaseGA4();
                }).catch(() => {
                    this.error = true;
                }).finally(() => {
                    this.loading = false;
                });
            },
            trackPurchaseGA4: async function() {
                try {
                    const isEvento = this.resumen.tipo === 'eventos';
                    const isAbono = this.resumen.tipo === 'abonos';

                    // Route to appropriate tracking based on purchase type
                    if (isEvento) {
                        const { trackPurchase } = useEventoTracking({ store: this.$store, getVuexAlias: this.config.getVuexAlias });

                        // Build resumen in GA4 format for evento
                        // Map backend structure (resumen.evento.*) to tracker contract
                        const ga4Resumen = {
                            evento_id: this.resumen.evento?.id,
                            evento_name: this.resumen.evento?.nombre || 'Evento',
                            entradas: this.resumen.entradas || [],
                            datos_compradores: this.resumen.entradas || [],
                            productos: [],
                            total_price: this.resumen.importe_total || 0,
                        };

                        await trackPurchase(
                            ga4Resumen,
                            String(this.resumen.tpv_order),
                            this.session_id
                        );
                    } else if (isAbono) {
                        const { trackPurchase } = useAbonoTracking({ store: this.$store, getVuexAlias: this.config.getVuexAlias });

                        // Build resumen in GA4 format for abono
                        // Map SGA backend structure to tracker's expected abonoResumen format
                        const firstAbonado = this.resumen.abonados?.[0];
                        const ga4Resumen = {
                            abono_id: firstAbonado?.lista?.id,
                            abono_name: firstAbonado?.lista?.nombre || 'Abono',
                            lista_abonados: this.resumen.abonados || [],
                            resumen_total: this.resumen.importe_total || 0,
                            moneda: 'EUR',
                            tpv_order: this.resumen.tpv_order,
                        };

                        await trackPurchase(
                            ga4Resumen,
                            String(this.n_pedido),
                            this.session_id
                        );
                    } else if (this.resumen.tipo === 'membresias' || this.resumen.tipo === 'renovacion') {
                        // Membresías (incluida la renovación, cuyo resumen usa
                        // tipo 'renovacion'): mismo contrato de abono (un item
                        // por membresía con su importe). El resumen de
                        // membresías no tiene lista_abonados, así que se mapea
                        // a items.
                        const { trackPurchase } = useAbonoTracking({ store: this.$store, getVuexAlias: this.config.getVuexAlias });

                        const ga4Resumen = {
                            abono_id: this.resumen.membresias?.[0]?.id,
                            abono_name: this.resumen.membresias?.[0]?.nombre || 'Membresía',
                            lista_abonados: this.resumen.membresias || [],
                            resumen_total: this.resumen.importe_total || 0,
                            moneda: 'EUR',
                            tpv_order: this.resumen.tpv_order,
                        };

                        await trackPurchase(
                            ga4Resumen,
                            String(this.n_pedido),
                            this.session_id
                        );
                    } else {
                        console.warn('[SGA CompraFinalizada] Unknown purchase type:', this.resumen.tipo);
                    }
                } catch (err) {
                    console.error('[SGA CompraFinalizada] GA4 tracking failed:', err);
                    // Don't break the page if tracking fails
                }
            }
        },
        computed: {
            configuracion() {
                return this.$store.getters.getConfig;
            },
            manager() {
                return this.$store.getters.getFooter;
            },
            /**
             * Detect if this is a renewal flow.
             */
            isRenewal() {
                return this.$route.query.tipo === 'renovacion' || this.resumen?.tipo === 'renovacion';
            },
            /**
             * Conditional page title based on purchase type.
             */
            pageTitle() {
                return this.isRenewal
                    ? this.$t('gracias.renovacion_confirmada')
                    : this.$t('gracias.compra_confirmada');
            },
            /**
             * Conditional CTA button text based on purchase type.
             */
            ctaButtonText() {
                return this.isRenewal
                    ? this.$t('gracias.volver_perfil')
                    : this.$t('gracias.volver');
            },
            /**
             * Conditional CTA button route based on purchase type.
             */
            ctaButtonRoute() {
                return this.isRenewal
                    ? { name: 'perfil' }
                    : { name: 'front.home' };
            }
        },
        created() {
            this.getResumen();
        },
        beforeDestroy() {
            if (this.pollTimer) {
                clearTimeout(this.pollTimer);
                this.pollTimer = null;
            }
        },
        components: {
            PageTitle, FirstBanner, DatosAbono, DatosEntrada, DatosMembresia
        }
    }
</script>
